import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ChatBotPanel from "./ChatBotPanel";
import varunImg from "./varun.webp";

const NOVA_STAR_PATH =
  "M12 1.6c.55 5 2.85 7.3 7.85 7.85-5 .55-7.3 2.85-7.85 7.85-.55-5-2.85-7.3-7.85-7.85 5-.55 7.3-2.85 7.85-7.85Z";

export default function ChatBot() {
  const subscribedFlag = useSelector((state) => state.subscribed);
  const userSubscriptions = useSelector((state) => state.user?.active_subscription);
  // Derive subscribed from both the boolean flag AND actual subscription data.
  // This ensures the chatbot stays in sync with the sidebar even if the flag
  // is stale (e.g. after a fresh purchase + reload where a 402 interceptor may reset it).
  const subscribed = subscribedFlag || (Array.isArray(userSubscriptions) && userSubscriptions.length > 0);
  const [isOpen, setIsOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const panelRef = useRef(null);

  // Pulse 3x on first login only (check localStorage)
  useEffect(() => {
    const hasSeenNova = localStorage.getItem("nova_seen");
    if (!hasSeenNova) {
      setShowPulse(true);
      // Stop pulsing after the 3 cycles (3 × 1.4s = 4.2s)
      const timer = setTimeout(() => {
        setShowPulse(false);
        localStorage.setItem("nova_seen", "0");
      }, 4200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // Mark as seen on first open
    if (!localStorage.getItem("nova_seen")) {
      localStorage.setItem("nova_seen", "1");
      setShowPulse(false);
    }
  }, []);

  const handleClose = useCallback(() => {
    panelRef.current?.cancelStream();
    setIsOpen(false);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleOpen();
      }
    },
    [handleOpen]
  );

  useEffect(() => {
    return () => {
      panelRef.current?.cancelStream();
    };
  }, []);

  return (
    <div className={`nova-widget ${isOpen ? "is-open" : ""}`}>
      {isOpen ? (
        <>
          <div className="nova-scrim" onClick={handleClose} aria-hidden="true" />
          <ChatBotPanel
            ref={panelRef}
            subscribed={subscribed}
            onClose={handleClose}
          />
        </>
      ) : (
        <button
          type="button"
          className={`nova-launcher ${showPulse ? "pulse" : ""}`}
          aria-label="Open Nova chat"
          onClick={handleOpen}
          onKeyDown={handleKeyDown}
        >
          <img
            src={varunImg}
            alt="Ask Nova"
            className="nova-launcher-avatar"
          />
          <span className="nova-launcher-badge" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d={NOVA_STAR_PATH} />
            </svg>
          </span>
          <span className="nova-launcher-label">Ask Nova</span>
        </button>
      )}
    </div>
  );
}
